import { dates } from "@omegagrid/core/dist/common";
import { PeriodDimensionType } from "@reporting/rp-types/analytics";
import { addMonths, compareAsc, endOfMonth, subYears, Day, differenceInDays, addDays, startOfWeek } from "date-fns";

/**
 * Period strings:
 * - static with month grouping (PM)
 *   - year: yyyy (2020)
 *   - quarter: yyyyQ (20201)
 *   - month: yyyyMM (202001)
 *   - day: yyyyMMdd (20200101)
 *
 * - static with week grouping (PW)
 *   - year: yyyy (2020)
 *   - week: yyyyWWW (2020001)
 *   - day: yyyyMMdd (20200101)
 *
 * - interval (PX):
 *   - static: <static>:<static> (2020:2020 = 2020-01-01:2020-12-31)
 *   - dynamic: <period_type><+/->:<period_type><+/-> (M-1:M+1 = last month to next month)
 *      - period types: D (day), W (week), M (month), Q (quarter), Y (year)
 *
 * - common periods (PC):
 *   - **** = any year
 *   - ****1 = any 1. quarter
 *   - ****01 = any January
 *   - ****001 = any 1. week
 *   - ********1 = any Monday
 */
export type PeriodString = string;

export type PeriodIntervalType = 'D'|'W'|'M'|'Q'|'Y';
export type DynamicPeriodBoundry = 'D'|'W'|'M'|'Q'|'Y';

export type PeriodParsingOptions = {
	/**
	 * Used for offsetting fiscal year.
	 *  - 0: fiscal years is aligned with calendar year
	 *  - 1: fiscal year starts in February
	 *  - -1: fiscal year starts in December of the previous year
	 */
	monthOffset?: number,
	weekStart?: Day, // 0 = Sunday, 1 = Monday, etc.
	baseDate?: Date, // default to today
};

export type PeriodInterval = {
	start: Date,
	end: Date,
};

export type ParsedPeriodBoundary = {
	def?: string,
	type?: PeriodIntervalType,
	offset?: number,
	date?: Date,
	fy?: number, // fiscal year
};

export type ParsedPeriod = {
	start?: ParsedPeriodBoundary,
	end?: ParsedPeriodBoundary,
	granularity?: PeriodIntervalType,
};

function getOptions(options?: PeriodParsingOptions) {
	return {
		monthOffset: options?.monthOffset ?? 0,
		weekStart: options?.weekStart ?? 1,
		baseDate: dates.stripTime(options?.baseDate ?? new Date()),
	};
}

export function getPeriodIntervalType(staticPeriod: string) {
	return {4: 'Y', 5: 'Q', 6: 'M', 7: 'W', 8: 'D'}[staticPeriod.length];
}

export function parseStaticMonthlyPeriod(period: string, options?: PeriodParsingOptions): ParsedPeriod {
	const type = getPeriodIntervalType(period);
	const { monthOffset } = getOptions(options);
	const y = parseInt(period.substring(0, 4));
	const q = type == 'Q' ? parseInt(period.substring(4, 5)) : null;
	const m = ['M', 'D'].includes(type) ? parseInt(period.substring(4, 6)) : null;
	const d = type == 'D' ? parseInt(period.substring(6, 8)) : null;

	let start: Date, end: Date;

	switch (getPeriodIntervalType(period)) {
		case 'Y':
			start = new Date(y, 0 + monthOffset, 1);
			end = new Date(y, 12 + monthOffset, 0);
			break;
		case 'Q':
			start = new Date(y, (q - 1) * 3 + monthOffset, 1);
			end = new Date(y, q * 3 + monthOffset, 0);
			break;
		case 'M':
			start = new Date(y, m - 1, 1);
			end = new Date(y, m, 0);
			break;
		case 'D':
			start = new Date(y, m - 1, d);
			end = new Date(y, m - 1, d);
			break;
	}
	return {
		start: {date: start, def: period},
		end: {date: end, def: period}
	};
}

export function parseStaticWeeklyPeriod(period: string, options?: PeriodParsingOptions): ParsedPeriod {
	const { monthOffset, weekStart } = getOptions(options);
	const y = parseInt(period.substring(0, 4));
	const yStart = getFiscalISOWeekYearStart(y, monthOffset, weekStart);
	const yEnd = getFiscalISOWeekYearEnd(y, monthOffset, weekStart);
	const type = getPeriodIntervalType(period);
	let start: Date, end: Date;
	switch (type) {
		case 'Y':
			start = yStart;
			end = yEnd;
			break;
		case 'W':
			const w = parseInt(period.substring(5));
			start = addDays(yStart, (w - 1) * 7);
			end = addDays(start, 6);
			break;
		case 'D':
			const m = parseInt(period.substring(4, 6));
			const d = parseInt(period.substring(6, 8));
			start = new Date(y, m - 1, d);
			end = new Date(y, m - 1, d);
			break;
		default:
			throw Error(`Unsupported period type: ${type}`);
	}

	return {
		start: {date: start, def: period},
		end: {date: end, def: period}
	};
}

function mod(n: number, d: number) {
	let r = n % d;
	if (r < 0) r += Math.abs(d);
	return r;
}

export function getFiscalYearBounds(year: number, monthOffset: number): PeriodInterval {
	const date = new Date(year, 0, 1, 0, 0, 0, 0);
	let min = new Date(year, mod(monthOffset, 12), 1, 0, 0, 0, 0);
	if (compareAsc(date, min) < 0) min = subYears(min, 1);
	return {start: min, end: dates.stripTime(endOfMonth(addMonths(new Date(min), 11)))};
}

export function getFiscalYearStart(year: number, monthOffset: number): Date {
	return getFiscalYearBounds(year, monthOffset).start!;
}

export function getFiscalYearEnd(year: number, monthOffset: number): Date {
	return getFiscalYearBounds(year, monthOffset).end!;
}

export function getFiscalISOWeekYearStart(year: number, monthOffset: number, weekStart: Day): Date {
	const start  = getFiscalYearStart(year, monthOffset);
	const dwStart = startOfWeek(start, {weekStartsOn: weekStart});
	return differenceInDays(start, dwStart) >= 3 ? addDays(dwStart, 7) : dwStart;
}

export function getFiscalISOWeekYearEnd(year: number, monthOffset: number, weekStart: Day): Date {
	const end = getFiscalYearEnd(year, monthOffset);
	const dwEnd = startOfWeek(end, {weekStartsOn: weekStart});
	return differenceInDays(end, dwEnd) >= 3 ? addDays(dwEnd, 6) : dwEnd;
}

export function parsePeriodBoundary(period: string): ParsedPeriodBoundary {
	const match = period.match(/^([DWMQY])([+-]\d+?)?$/i);
	if (!match) throw Error(`Invalid dynamic period boundary: ${period}`);
	return {type: match[1] as PeriodIntervalType, offset: parseInt(match[2] || '0')};
}

export function parseLowerDynamicPeriodBoundary(period: string, options?: PeriodParsingOptions): ParsedPeriodBoundary {
	const { monthOffset, weekStart, baseDate } = getOptions(options);
	const boundary = parsePeriodBoundary(period);
	switch (boundary.type) {
		case 'D':
			boundary.date = addDays(baseDate, boundary.offset);
			break;
		case 'W': {
			const startOfWeekDate = startOfWeek(baseDate, {weekStartsOn: weekStart});
			boundary.date = addDays(startOfWeekDate, boundary.offset * 7);
			break;
		}
		case 'M':
			boundary.date = addMonths(new Date(baseDate.getFullYear(), baseDate.getMonth(), 1), boundary.offset);
			break;
		case 'Q': {
			const currentQuarter = Math.floor(baseDate.getMonth() / 3);
			boundary.date = addMonths(new Date(baseDate.getFullYear(), currentQuarter * 3, 1), boundary.offset * 3);
			break;
		}
		case 'Y': {
			const year = baseDate.getFullYear();
			const fyStart = getFiscalYearStart(year, monthOffset);
			boundary.date = addMonths(fyStart, boundary.offset * 12);
			break;
		}
	}
	return boundary;
}

export function parseUpperDynamicPeriodBoundary(period: string, options?: PeriodParsingOptions): ParsedPeriodBoundary {
	const { monthOffset, weekStart, baseDate } = getOptions(options);
	const boundary = parsePeriodBoundary(period);
	switch (boundary.type) {
		case 'D':
			boundary.date = addDays(baseDate, boundary.offset);
			break;
		case 'W': {
			const startOfWeekDate = startOfWeek(baseDate, {weekStartsOn: weekStart});
			boundary.date = addDays(startOfWeekDate, boundary.offset * 7 + 6);
			break;
		}
		case 'M': {
			const date = addMonths(new Date(baseDate.getFullYear(), baseDate.getMonth(), 1), boundary.offset);
			boundary.date = endOfMonth(date);
			break;
		}
		case 'Q': {
			const currentQuarter = Math.floor(baseDate.getMonth() / 3);
			const date = addMonths(new Date(baseDate.getFullYear(), currentQuarter * 3, 1), boundary.offset * 3);
			boundary.date = endOfMonth(addMonths(date, 2));
			break;
		}
		case 'Y': {
			const year = baseDate.getFullYear();
			const fyEnd = getFiscalYearEnd(year, monthOffset);
			boundary.date = addMonths(fyEnd, boundary.offset * 12);
			break;
		}
	}
	return boundary;
}

export const isDynamicPeriodBoundary = (period: string) => /^[a-z]/i.test(period);

export function parseIntervalPeriod(period: string, options?: PeriodParsingOptions): ParsedPeriod {
	if (!period.includes(':')) period = `${period}:${period}`;
	const parts = period.split(':');
	const start = isDynamicPeriodBoundary(parts[0]) ? parseLowerDynamicPeriodBoundary(parts[0], options) : parseStaticMonthlyPeriod(parts[0], options).start!;
	const end = isDynamicPeriodBoundary(parts[1]) ? parseUpperDynamicPeriodBoundary(parts[1], options) : parseStaticMonthlyPeriod(parts[1], options).end!;
	start.date = dates.stripTime(start.date!);
	end.date = dates.stripTime(end.date!);
	const granularity = parts.length > 2 ? parts[2] as PeriodIntervalType : undefined;

	return { start, end, granularity };
}

export function parsePeriod(period: string, type: PeriodDimensionType, options?: PeriodParsingOptions): ParsedPeriod {
	if (type === 'PM') return parseStaticMonthlyPeriod(period, options);
	if (type === 'PW') return parseStaticWeeklyPeriod(period, options);
	if (type === 'PX') return parseIntervalPeriod(period, options);
	return null;
}
